import React, { createContext, useContext, useState, useEffect } from "react";
import { activeLocales, localeConfig, translations, Language, TranslationKey } from "@/lib/i18n";

type I18nContextType = {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: TranslationKey) => string;
  isRTL: boolean;
};

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem("souq_lang") : null;
    return (saved === "ar" ? "ar" : "en") as Language;
  });

  useEffect(() => {
    document.documentElement.dir = localeConfig[language].dir;
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    if (activeLocales.includes(lang)) localStorage.setItem("souq_lang", lang);
  };

  const t = (key: TranslationKey): string => {
    return (translations[language] as Record<string, string>)[key] ?? (translations.en as Record<string, string>)[key] ?? key;
  };

  return (
    <I18nContext.Provider value={{ language, setLanguage, t, isRTL: localeConfig[language].dir === "rtl" }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within an I18nProvider");
  }
  return context;
}
